import React, { FC, useEffect, useState } from 'react'
import { useOrganization } from '../context/OrganizationContext';
import { useText } from '../context/TextContext';
import { Button } from 'react-bootstrap';
import { AdvancedButton } from '../components/AdvancedButton';
import { Link, useNavigate } from 'react-router-dom';
import { DealStep } from '../models/shared/DealStep';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';
import { useFirestore } from 'reactfire';
import { doc, setDoc } from 'firebase/firestore';
import { COLLECTIONS, DND_ACTIVATION_DISTANCE_CONSTRAINT } from '../utils/shared/constants';
import { Organization } from '../models/shared/Organization';
import PipelineStepEdit from '../components/PipelineStepEdit';
import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import PageHeader from '../components/PageHeader';

interface IProps { }

export const PipelineEditPage: FC<IProps> = (props) => {
    const { organization, orgId } = useOrganization();
    const text = useText();
    const [steps, setSteps] = useState<DealStep[]>(organization.dealSteps);
    const [dirty, setDirty] = useState(false);
    const [newStep, setNewStep] = useState<DealStep | null>(null);
    const [processing, setProcessing] = useState(false);
    const firestore = useFirestore();
    const navigate = useNavigate();
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: DND_ACTIVATION_DISTANCE_CONSTRAINT }),
    );

    useEffect(() => {
        setSteps(organization.dealSteps);
    }, [organization.dealSteps])

    const handleUpdateStep = (id: string, update: Partial<DealStep>) => {
        const newSteps = steps.map(step => {
            if (step.id === id) {
                return { ...step, ...update };
            }
            return step;
        });
        setSteps(newSteps);
        setDirty(true);
    }

    const handleNewStep = () => {
        const newStep: DealStep = {
            name: text.newStep,
            id: uuid(),
        };
        const newSteps: DealStep[] = [...steps, newStep];
        setSteps(newSteps);
        setDirty(true);
        setNewStep(newStep);
    };

    const hanldeRemoveStep = (id: string) => {
        const newSteps = steps.filter(step => step.id !== id);
        setSteps(newSteps);
        setDirty(true);
    }

    const hanldeSave = async () => {
        setProcessing(true);
        try {
            const orgUpdate: Partial<Organization> = {
                dealSteps: steps
            }
            await setDoc(doc(firestore, COLLECTIONS.ORGANIZATIONS, orgId), orgUpdate, { merge: true });
            toast.success(text.defaultSuccess);
            navigate('/');
            return;
        } catch (e) {
            console.error(e);
            toast.error(text.defaultError)
        }

        setProcessing(false);
        setDirty(false);
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over) {
            return;
        }

        if (active.id !== over.id) {
            const oldIndex = steps.findIndex(step => step.id === active.id);
            const newIndex = steps.findIndex(step => step.id === over.id);
            const newSteps = [...steps];
            newSteps.splice(oldIndex, 1);
            newSteps.splice(newIndex, 0, steps[oldIndex]);
            setSteps(newSteps);
            setDirty(true);
        }
    }

    return (
        <div className="d-flex h-100 flex-column gap-4 container-fluid">
            <PageHeader
                title={text.editPipeline}
                description={text.editPipelineDescription}
                showBackButton={true}
                actionsPrefix={
                    <Link to={'/'}><Button variant='outline-secondary'>{text.cancel}</Button></Link>
                }
                actions={[
                    {
                        text: text.save,
                        icon: 'bi-floppy',
                        processing: processing,
                        onClick: () => hanldeSave(),
                        disabled: !dirty
                    }
                ]}
            />

            <div className='d-flex flex-1 gap-4 row m-0 flex-nowrap align-items-start overflow-auto'>
                <DndContext sensors={sensors}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={steps.map(step => step.id)} strategy={horizontalListSortingStrategy}>

                        {steps.map((step) => (
                            <PipelineStepEdit key={step.id} step={step} onUpdate={handleUpdateStep} onRemove={hanldeRemoveStep} focus={step.id === newStep?.id} />
                        ))}
                    </SortableContext>
                </DndContext>

                <div className='col p-3 border border-secondary rounded center flex-column gap-3 text-secondary'>
                    <h6 className='m-0'>{text.addStep}</h6>
                    <p className='m-0 text-center'>{text.addStepDescription}</p>
                    <AdvancedButton onClick={() => handleNewStep()} icon='bi-plus' variant='secondary' size='sm'>{text.newStep}</AdvancedButton>
                </div>
            </div>
        </div>
    )
}
